<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import { uploadImage } from "@/lib/uploads";
import { fetchNewsEvent, addNewsEvent, saveNewsEvent } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const form = ref({ title: "", content: "", url: "", sequence: "", imageUrl: "", isEnabled: false, isDynamic: false, showTitle: true, showDescription: true });
const imageUploader = (file, p) => uploadImage("news-events", file, p);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const n = await fetchNewsEvent(id.value);
    if (!n) { notFound.value = true; return; }
    const m = n.message || {};
    form.value = { title: m.title ?? "", content: m.desc ?? "", url: m.url ?? "", sequence: n.sequence ?? "", imageUrl: m.imageUrl ?? "", isEnabled: !!(n.isEnabled ?? n.isExternalBrowser), isDynamic: !!n.isDynamic, showTitle: n.showTitle !== false, showDescription: n.showDescription !== false };
  } catch { error.value = "Couldn't load the item."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.title.trim()) { validationError.value = "Enter a title."; return; }
  if (!f.imageUrl) { validationError.value = "Upload an image."; return; }
  const base = { title: f.title.trim(), desc: f.content, url: f.url, imageUrl: f.imageUrl, sequence: f.sequence === "" ? null : Number(f.sequence), isEnabled: f.isEnabled, isDynamic: f.isDynamic, showTitle: f.showTitle, showDescription: f.showDescription };
  saving.value = true;
  try {
    if (isEdit.value) await saveNewsEvent({ ...base, id: id.value, isExternalBrowser: f.isEnabled });
    else await addNewsEvent(base);
    success.value = true; setTimeout(() => router.push({ name: "news-events" }), 800);
  } catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'news-events' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit News & Event" : "Add News & Event" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Item not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12"><VTextField v-model="form.title" label="Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Content" :image-uploader="imageUploader" /></VCol>
          <VCol cols="12"><VTextField v-model="form.url" label="Link URL" hint="Optional." /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.sequence" label="Sequence" type="number" /></VCol>
          <VCol cols="12"><UploadField v-model="form.imageUrl" kind="image" label="Image" :uploader="imageUploader" /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isEnabled" label="Enabled" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isDynamic" label="Dynamic User ID" hide-details /></VCol>
          <VCol cols="6"><VCheckbox v-model="form.showTitle" label="Show Title" hide-details /></VCol>
          <VCol cols="6"><VCheckbox v-model="form.showDescription" label="Show Description" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add Item" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'news-events' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
